/**
 * Sortable image grid using @dnd-kit
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { ImageCard } from './ImageCard';
import { UploadZone } from './UploadZone';
import type { ListingImage } from '../types';

interface ImageGridProps {
  images: ListingImage[];
  autoHeroId: string | null;
  onReorder: (newOrder: string[]) => void;
  onUpload: (files: File[]) => void;
  onDelete: (imageId: string) => void;
  onSetHero: (imageId: string) => void;
  onCopyUrl: (url: string) => void;
  onImageClick: (index: number) => void;
  disabled?: boolean;
}

export function ImageGrid({
  images,
  autoHeroId,
  onReorder,
  onUpload,
  onDelete,
  onSetHero,
  onCopyUrl,
  onImageClick,
  disabled = false,
}: ImageGridProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = [...images];
        const [removed] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, removed);
        onReorder(newOrder.map((img) => img.id));
      }
    }
  };

  return (
    <DndContext
      sensors={disabled ? [] : sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={images.map((img) => img.id)} strategy={rectSortingStrategy}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px',
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'default',
            transition: 'opacity 0.2s',
          }}
          title={disabled ? 'Drag disabled during upload' : undefined}
        >
          {images.map((image, index) => (
            <ImageCard
              key={image.id}
              image={image}
              isAutoHero={image.id === autoHeroId}
              onDelete={() => onDelete(image.id)}
              onSetHero={() => onSetHero(image.id)}
              onCopyUrl={() => onCopyUrl(image.url)}
              onImageClick={() => onImageClick(index)}
            />
          ))}
          <UploadZone onUpload={onUpload} disabled={disabled} />
        </div>
      </SortableContext>
    </DndContext>
  );
}
